import { Inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import * as _ from 'lodash';
import {
  RbacService,
  UserInfoViewModel,
} from '../../common-api/system-management-api';
import {
  CacheService,
  MEMORY_CACHE_SERVICE,
} from '../../common/services/cache.service';

const USER_INFO_CACHE_KEY = 'userInfo';

/**
 * 附加使用者資訊服務
 *
 * @export
 * @class UserComposerService
 */
@Injectable({
  providedIn: 'root',
})
export class UserComposerService {
  constructor(
    private rbacService: RbacService,
    @Inject(MEMORY_CACHE_SERVICE) private cacheService: CacheService
  ) {}

  /**
   * 附加使用者名稱至原資料結構列(多筆)
   *
   * @template T
   * @param {T[]} rawData
   * @param {string[]} userNoProps
   * @return {*}  {Observable<UserComposerData<T>[]>}
   * @memberof UserComposerService
   */
  appendUserInfos<T>(
    rawData: T[],
    userNoProps: string[]
  ): Observable<UserComposerData<T>[]> {
    const cachedUserInfos = this.getCachedUserInfos();
    const userNos = _.uniq(
      rawData.reduce(
        (array, item: any) => array.concat(userNoProps.map((x) => item[x])),
        [] as string[]
      )
    );
    const uncoveredUserNos = userNos.filter(
      (x: any) => cachedUserInfos.findIndex((y) => y.userNo === x) < 0
    );
    let userInfos = cachedUserInfos.filter((x) =>
      userNos.includes(x.userNo as any)
    );
    if (uncoveredUserNos.length > 0) {
      const observable = this.rbacService
        .rbacUserProfileUserNoSearchPost(uncoveredUserNos)
        .pipe(
          map((results) => {
            this.saveToCache(results);
            userInfos = userInfos.concat(results);
            const userComposerData = this.attachToUserInfos(
              rawData,
              userNoProps,
              userInfos
            );
            return userComposerData;
          })
        );
      return observable;
    } else {
      const userComposerData = this.attachToUserInfos(
        rawData,
        userNoProps,
        userInfos
      );
      const observable = of(userComposerData);
      return observable;
    }
  }

  /**
   * 附加使用者名稱至原資料結構列(單筆)
   *
   * @template T
   * @param {T} rawData
   * @param {string[]} userNoProps
   * @return {*}  {Observable<UserComposerData<T>>}
   * @memberof UserComposerService
   */
  appendUserInfo<T>(
    rawData: T,
    userNoProps: string[]
  ): Observable<UserComposerData<T>> {
    const cachedUserInfos = this.getCachedUserInfos();
    const userNos = _.uniq(userNoProps.map((x) => rawData[x as keyof T]));
    const uncoveredUserNos = userNos.filter(
      (x: any) => cachedUserInfos.findIndex((y) => y.userNo === x) < 0
    );
    let userInfos = cachedUserInfos.filter((x) =>
      userNos.includes(x.userNo as any)
    );
    if (uncoveredUserNos.length > 0) {
      const observable = this.rbacService
        .rbacUserProfileUserNoSearchPost(uncoveredUserNos as any)
        .pipe(
          map((results) => {
            this.saveToCache(results);
            userInfos = userInfos.concat(results);
            return this.attachUserInfo(rawData, userNoProps, userInfos);
          })
        );
      return observable;
    } else {
      const userComposerData = this.attachUserInfo(
        rawData,
        userNoProps,
        userInfos
      );
      const observable = of(userComposerData);
      return observable;
    }
  }

  /**
   * 取得使用者資訊
   *
   * @param {string} userNo
   * @return {*}  {Observable<UserProfileViewModel>}
   * @memberof UserComposerService
   */
  getUserInfo(userNo: string): Observable<UserInfoViewModel | undefined> {
    const cachedUserInfos = this.getCachedUserInfos();
    const idx = cachedUserInfos.findIndex((x) => x.userNo === userNo);
    return idx < 0
      ? this.rbacService
          .rbacUserProfileUserNoGet(userNo)
          .pipe(map((results) => results))
      : of(cachedUserInfos[idx]);
  }

  /**
   * 產生合併使用者名稱的資料結構(多筆)
   *
   * @private
   * @template T
   * @param {T[]} rawData
   * @param {string[]} userNoProps
   * @param {UserInfoViewModel[]} userInfos
   * @return {*}  {UserComposerData<T>[]}
   * @memberof UserComposerService
   */
  private attachToUserInfos<T>(
    rawData: T[],
    userNoProps: string[],
    userInfos: UserInfoViewModel[]
  ): UserComposerData<T>[] {
    const withComposerData = rawData.map((x) => {
      return this.attachUserInfo(x, userNoProps, userInfos);
    });
    return withComposerData;
  }

  /**
   * 產生合併使用者名稱的資料結構(單筆)
   *
   * @private
   * @template T
   * @param {T} rawData
   * @param {string[]} userNoProps
   * @param {UserInfoViewModel[]} userInfos
   * @return {*}  {UserComposerData<T>}
   * @memberof UserComposerService
   */
  private attachUserInfo<T>(
    rawData: T,
    userNoProps: string[],
    userInfos: UserInfoViewModel[]
  ): UserComposerData<T> {
    const obj: UserComposerInfo = {};
    userNoProps.forEach((prop) => {
      const user = userInfos.find((y) => y.userNo === rawData[prop as keyof T]);
      obj[`${prop}ComposerName`] = user?.userName;
    });

    const data = { ...rawData, ...obj };
    return data;
  }

  /**
   * 取得快取中的使用者資訊
   *
   * @private
   * @return {*}  {UserInfoViewModel[]}
   * @memberof UserComposerService
   */
  private getCachedUserInfos(): UserInfoViewModel[] {
    const cachedUserInfos =
      this.cacheService.get<UserInfoViewModel[]>(USER_INFO_CACHE_KEY) ?? [];
    return cachedUserInfos;
  }

  /**
   * 儲存使用者資訊至快取
   *
   * @private
   * @param {UserInfoViewModel[]} userInfos
   * @memberof UserComposerService
   */
  private saveToCache(userInfos: UserInfoViewModel[]): void {
    const cachedUserInfos = this.getCachedUserInfos();
    // 更新 cache 資料
    userInfos.forEach((x) => {
      const idx = cachedUserInfos.findIndex((y) => y.userNo === x.userNo);
      idx > -1 ? (cachedUserInfos[idx] = x) : cachedUserInfos.push(x);
    });
    this.cacheService.add(USER_INFO_CACHE_KEY, cachedUserInfos);
  }
}

export interface UserComposerInfo {
  [prop: string]: string | undefined;
}

declare type UserComposerData<T> = T & UserComposerInfo;
